import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  UserMinus, Search, AlertTriangle, DollarSign, Wallet,
  ArrowLeft, Plus, Trash2, CheckCircle, Printer, Building2,
  Ban, CheckSquare, Info
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { formatDate } from '../../utils/dateTimeUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function StudentWithdrawal() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [refundDetails, setRefundDetails] = useState(null);
  const [banks, setBanks] = useState([]);
  const [reasons, setReasons] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [showReceipt, setShowReceipt] = useState(false);
  const [receiptData, setReceiptData] = useState(null);
  
  const [formData, setFormData] = useState({
    reason: '',
    reason_details: '',
    // Refund amounts (editable by admin)
    refund_caution_money: 0,
    refund_admission_fee: 0,
    refund_semester_fee: 0,
    // Deductions
    deductions: [],
    refund_mode: 'Cash',
    bank_id: '',
    bank_name: '',
    transaction_ref: '',
    remarks: '',
    withdrawal_date: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    fetchBanks();
    fetchReasons();
  }, []);

  const fetchBanks = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/accounts/banks`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setBanks(data || []);
      }
    } catch (error) {
      console.error('Error fetching banks:', error);
    }
  };

  const fetchReasons = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/student-withdrawal/reasons`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setReasons(data.reasons || []);
      }
    } catch (error) {
      console.error('Error fetching reasons:', error);
    }
  };

  const searchStudents = async () => {
    if (!searchQuery.trim()) return;
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/students/?search=${encodeURIComponent(searchQuery)}&limit=10`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        const activeStudents = data.filter(s => s.status !== 'Withdrawn');
        setSearchResults(activeStudents);
        if (activeStudents.length === 0) {
          toast.info('No active students found');
        }
      }
    } catch (error) {
      console.error('Error searching students:', error);
      toast.error('Search failed');
    } finally {
      setLoading(false);
    }
  };

  const selectStudent = async (student) => {
    setSelectedStudent(student);
    setSearchResults([]);
    setSearchQuery('');
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/student-withdrawal/student/${student.id}/refund-details`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setRefundDetails(data);
        
        // Auto-fill refundable amounts based on policy
        setFormData(prev => ({
          ...prev,
          refund_caution_money: data.fee_breakdown?.caution_money || 0,  // Full refund
          refund_admission_fee: data.fee_breakdown?.admission_fee || 0,  // Refundable
          refund_semester_fee: 0  // Admin decides - start with 0
        }));
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to fetch refund details');
        setSelectedStudent(null);
      }
    } catch (error) {
      console.error('Error fetching refund details:', error);
      toast.error('Failed to fetch refund details');
    }
  };

  const addDeduction = () => {
    setFormData(prev => ({
      ...prev,
      deductions: [...prev.deductions, { name: '', amount: 0 }]
    }));
  };

  const updateDeduction = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      deductions: prev.deductions.map((d, i) => 
        i === index ? { ...d, [field]: field === 'amount' ? parseFloat(value) || 0 : value } : d
      )
    }));
  };

  const removeDeduction = (index) => {
    setFormData(prev => ({
      ...prev,
      deductions: prev.deductions.filter((_, i) => i !== index)
    }));
  };

  const calculateGrossRefund = () => {
    return formData.refund_caution_money + formData.refund_admission_fee + formData.refund_semester_fee;
  };

  const calculateTotalDeductions = () => {
    return formData.deductions.reduce((sum, d) => sum + (d.amount || 0), 0);
  };

  const calculateNetRefund = () => {
    return Math.max(0, calculateGrossRefund() - calculateTotalDeductions());
  };

  const handleSubmit = async () => {
    if (!selectedStudent || !refundDetails) return;
    
    if (!formData.reason) {
      toast.error('Please select a reason for withdrawal');
      return;
    }
    
    if (formData.refund_mode !== 'Cash' && !formData.bank_id) {
      toast.error('Please select a bank for bank transfer');
      return;
    }
    
    const netRefund = calculateNetRefund();
    const confirmed = window.confirm(
      `Are you sure you want to process withdrawal for ${selectedStudent.full_name}?\n\n` +
      `Net Refund Amount: ₹${netRefund.toLocaleString('en-IN')}\n` +
      `Refund Mode: ${formData.refund_mode}\n\n` +
      `This action cannot be undone.`
    );
    
    if (!confirmed) return;
    
    setProcessing(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/student-withdrawal/process`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          student_id: selectedStudent.id,
          ...formData
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success('Withdrawal processed successfully!');
        setReceiptData({
          ...data,
          student: selectedStudent,
          refundDetails,
          formData
        });
        setShowReceipt(true);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to process withdrawal');
      }
    } catch (error) {
      console.error('Error processing withdrawal:', error);
      toast.error('Failed to process withdrawal');
    } finally {
      setProcessing(false);
    }
  };

  const resetForm = () => {
    setSelectedStudent(null);
    setRefundDetails(null);
    setShowReceipt(false);
    setReceiptData(null);
    setFormData({
      reason: '',
      reason_details: '',
      refund_caution_money: 0,
      refund_admission_fee: 0,
      refund_semester_fee: 0,
      deductions: [],
      refund_mode: 'Cash',
      bank_id: '',
      bank_name: '',
      transaction_ref: '',
      remarks: ''
    });
  };

  const printReceipt = () => {
    window.print();
  };

  // Receipt Component
  if (showReceipt && receiptData) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between no-print">
          <h1 className="text-2xl font-bold">Withdrawal Processed</h1>
          <div className="flex gap-2">
            <Button onClick={printReceipt}>
              <Printer className="h-4 w-4 mr-2" /> Print Receipt
            </Button>
            <Button variant="outline" onClick={resetForm}>
              New Withdrawal
            </Button>
          </div>
        </div>
        
        <Card className="max-w-2xl mx-auto print:shadow-none">
          <CardContent className="p-8">
            <div className="text-center mb-6">
              <h2 className="text-xl font-bold">STUDENT WITHDRAWAL RECEIPT</h2>
              <p className="text-gray-500">Reference: {receiptData.withdrawal_no}</p>
            </div>
            
            <div className="border-t border-b py-4 mb-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">Student Name</p>
                  <p className="font-medium">{receiptData.student_name}</p>
                </div>
                <div>
                  <p className="text-gray-500">Student ID</p>
                  <p className="font-medium">{receiptData.student?.student_id || receiptData.student?.admission_number}</p>
                </div>
                <div>
                  <p className="text-gray-500">Course</p>
                  <p className="font-medium">{receiptData.student?.course_name}</p>
                </div>
                <div>
                  <p className="text-gray-500">Date</p>
                  <p className="font-medium">{formatDate()}</p>
                </div>
              </div>
            </div>
            
            <div className="space-y-2 mb-4 text-sm">
              <div className="flex justify-between">
                <span>Caution Money Refund</span>
                <span>₹{formData.refund_caution_money?.toLocaleString('en-IN')}</span>
              </div>
              <div className="flex justify-between">
                <span>Admission Fee Refund</span>
                <span>₹{formData.refund_admission_fee?.toLocaleString('en-IN')}</span>
              </div>
              <div className="flex justify-between">
                <span>Semester Fee Refund</span>
                <span>₹{formData.refund_semester_fee?.toLocaleString('en-IN')}</span>
              </div>
              <div className="flex justify-between font-medium border-t pt-2">
                <span>Gross Refund</span>
                <span>₹{receiptData.gross_refund?.toLocaleString('en-IN')}</span>
              </div>
              {receiptData.total_deductions > 0 && (
                <div className="flex justify-between text-red-600">
                  <span>Total Deductions</span>
                  <span>- ₹{receiptData.total_deductions?.toLocaleString('en-IN')}</span>
                </div>
              )}
              <div className="flex justify-between text-lg font-bold border-t pt-2">
                <span>Net Refund Amount</span>
                <span className="text-green-600">₹{receiptData.net_refund?.toLocaleString('en-IN')}</span>
              </div>
              <div className="flex justify-between text-sm text-gray-500">
                <span>Refund Mode</span>
                <span>{receiptData.refund_mode}</span>
              </div>
            </div>
            
            {receiptData.consultant_payout_blocked && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 text-sm">
                <div className="flex items-center gap-2 text-amber-700 font-medium">
                  <AlertTriangle className="h-4 w-4" />
                  Consultant Payout Blocked: {receiptData.consultant_name} (₹{receiptData.blocked_commission?.toLocaleString('en-IN')})
                </div>
              </div>
            )}
            
            <div className="border-t pt-4 mt-6 text-center text-sm text-gray-500">
              <p>This is a computer generated receipt</p>
              <p className="mt-4">Processed By: ___________________</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="outline" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4 mr-2" /> Back
        </Button>
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Student Withdrawal</h1>
          <p className="text-gray-600 mt-1">Process student withdrawal with refund</p>
        </div>
      </div>

      {/* Refund Policy Info */}
      <Card className="border-blue-200 bg-blue-50">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Info className="h-5 w-5 text-blue-600 mt-0.5" />
            <div className="text-sm">
              <p className="font-medium text-blue-800 mb-1">Refund Policy:</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-blue-700">
                <span className="flex items-center gap-1"><Ban className="h-3 w-3 text-red-500" /> Seat Booking: Non-refundable</span>
                <span className="flex items-center gap-1"><CheckSquare className="h-3 w-3 text-green-500" /> Admission Fee: Refundable</span>
                <span className="flex items-center gap-1"><Info className="h-3 w-3 text-amber-500" /> Semester Fee: Admin decides</span>
                <span className="flex items-center gap-1"><CheckSquare className="h-3 w-3 text-green-500" /> Caution Money: Full refund</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Search Section */}
      {!selectedStudent && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Search className="h-5 w-5" />
              Search Student
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-4">
              <div className="flex-1">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && searchStudents()}
                  placeholder="Enter student name, ID, or admission number..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  data-testid="student-search-input"
                />
              </div>
              <Button onClick={searchStudents} disabled={loading}>
                {loading ? 'Searching...' : 'Search'}
              </Button>
            </div>
            
            {searchResults.length > 0 && (
              <div className="mt-4 border rounded-lg divide-y">
                {searchResults.map((student) => (
                  <div
                    key={student.id}
                    className="p-3 hover:bg-gray-50 cursor-pointer flex justify-between items-center"
                    onClick={() => selectStudent(student)}
                  >
                    <div>
                      <p className="font-medium">{student.full_name}</p>
                      <p className="text-sm text-gray-500">
                        {student.student_id || student.admission_number} | {student.course_name}
                      </p>
                    </div>
                    <Button size="sm" variant="outline">Select</Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Selected Student & Fee Breakdown */}
      {selectedStudent && refundDetails && (
        <>
          {/* Student Info */}
          <Card className="border-blue-200 bg-blue-50">
            <CardContent className="p-4">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="text-lg font-bold text-blue-800">{refundDetails.student.full_name}</h3>
                  <p className="text-blue-600">
                    {refundDetails.student.student_id} | {refundDetails.student.course_name}
                  </p>
                  <p className="text-sm text-blue-500">
                    Father: {refundDetails.student.father_name} | Phone: {refundDetails.student.phone}
                  </p>
                </div>
                <Button variant="outline" size="sm" onClick={resetForm}>
                  Change Student
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Fee Breakdown - What Student Paid */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="h-5 w-5 text-blue-600" />
                Fee Breakdown (What Student Paid)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                  <div className="flex items-center gap-2 mb-1">
                    <Ban className="h-4 w-4 text-red-500" />
                    <span className="text-xs text-red-600 font-medium">NON-REFUNDABLE</span>
                  </div>
                  <p className="text-sm text-gray-600">Seat Booking</p>
                  <p className="text-xl font-bold text-gray-800">
                    ₹{refundDetails.fee_breakdown?.seat_booking?.toLocaleString('en-IN') || 0}
                  </p>
                </div>
                
                <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                  <div className="flex items-center gap-2 mb-1">
                    <CheckSquare className="h-4 w-4 text-green-500" />
                    <span className="text-xs text-green-600 font-medium">REFUNDABLE</span>
                  </div>
                  <p className="text-sm text-gray-600">Admission Fee</p>
                  <p className="text-xl font-bold text-gray-800">
                    ₹{refundDetails.fee_breakdown?.admission_fee?.toLocaleString('en-IN') || 0}
                  </p>
                </div>
                
                <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                  <div className="flex items-center gap-2 mb-1">
                    <Info className="h-4 w-4 text-amber-500" />
                    <span className="text-xs text-amber-600 font-medium">ADMIN DECIDES</span>
                  </div>
                  <p className="text-sm text-gray-600">Semester Fee</p>
                  <p className="text-xl font-bold text-gray-800">
                    ₹{refundDetails.fee_breakdown?.semester_fee?.toLocaleString('en-IN') || 0}
                  </p>
                </div>
                
                <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                  <div className="flex items-center gap-2 mb-1">
                    <CheckSquare className="h-4 w-4 text-green-500" />
                    <span className="text-xs text-green-600 font-medium">FULL REFUND</span>
                  </div>
                  <p className="text-sm text-gray-600">Caution Money</p>
                  <p className="text-xl font-bold text-gray-800">
                    ₹{refundDetails.fee_breakdown?.caution_money?.toLocaleString('en-IN') || 0}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Refund Amounts - Admin Editable */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Wallet className="h-5 w-5 text-green-600" />
                Refund Amounts (Enter amounts to refund)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Caution Money Refund
                    <span className="text-xs text-green-600 ml-1">(Max: ₹{refundDetails.fee_breakdown?.caution_money?.toLocaleString('en-IN') || 0})</span>
                  </label>
                  <input
                    type="number"
                    value={formData.refund_caution_money}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      refund_caution_money: Math.min(parseFloat(e.target.value) || 0, refundDetails.fee_breakdown?.caution_money || 0)
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    min="0"
                    max={refundDetails.fee_breakdown?.caution_money || 0}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Admission Fee Refund
                    <span className="text-xs text-green-600 ml-1">(Max: ₹{refundDetails.fee_breakdown?.admission_fee?.toLocaleString('en-IN') || 0})</span>
                  </label>
                  <input
                    type="number"
                    value={formData.refund_admission_fee}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      refund_admission_fee: Math.min(parseFloat(e.target.value) || 0, refundDetails.fee_breakdown?.admission_fee || 0)
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    min="0"
                    max={refundDetails.fee_breakdown?.admission_fee || 0}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Semester Fee Refund
                    <span className="text-xs text-amber-600 ml-1">(Max: ₹{refundDetails.fee_breakdown?.semester_fee?.toLocaleString('en-IN') || 0})</span>
                  </label>
                  <input
                    type="number"
                    value={formData.refund_semester_fee}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      refund_semester_fee: Math.min(parseFloat(e.target.value) || 0, refundDetails.fee_breakdown?.semester_fee || 0)
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    min="0"
                    max={refundDetails.fee_breakdown?.semester_fee || 0}
                  />
                </div>
              </div>
              
              <div className="mt-4 p-4 bg-green-100 rounded-lg flex justify-between items-center">
                <span className="font-medium">Gross Refund Amount</span>
                <span className="text-2xl font-bold text-green-700">
                  ₹{calculateGrossRefund().toLocaleString('en-IN')}
                </span>
              </div>
            </CardContent>
          </Card>

          {/* Deductions */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Trash2 className="h-5 w-5 text-red-500" />
                  Deductions (Optional)
                </span>
                <Button size="sm" variant="outline" onClick={addDeduction}>
                  <Plus className="h-4 w-4 mr-1" /> Add Deduction
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {formData.deductions.length === 0 ? (
                <p className="text-gray-500 text-center py-4">No deductions added</p>
              ) : (
                <div className="space-y-3">
                  {formData.deductions.map((deduction, index) => (
                    <div key={index} className="flex gap-3 items-center">
                      <select
                        value={deduction.name}
                        onChange={(e) => updateDeduction(index, 'name', e.target.value)}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
                      >
                        <option value="">Select Deduction Type</option>
                        <option value="Processing Fee">Processing Fee</option>
                        <option value="Library Fine">Library Fine</option>
                        <option value="Hostel Damage">Hostel Damage</option>
                        <option value="Lab Equipment">Lab Equipment</option>
                        <option value="ID Card">ID Card</option>
                        <option value="Other">Other</option>
                      </select>
                      <input
                        type="number"
                        value={deduction.amount}
                        onChange={(e) => updateDeduction(index, 'amount', e.target.value)}
                        className="w-32 px-3 py-2 border border-gray-300 rounded-lg"
                        placeholder="Amount"
                        min="0"
                      />
                      <Button variant="outline" size="sm" className="text-red-500" onClick={() => removeDeduction(index)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                  <div className="flex justify-between items-center pt-3 border-t">
                    <span className="font-medium text-red-600">Total Deductions</span>
                    <span className="font-bold text-red-600">₹{calculateTotalDeductions().toLocaleString('en-IN')}</span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Withdrawal Details */}
          <Card>
            <CardHeader>
              <CardTitle>Withdrawal Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Withdrawal Date *</label>
                  <input type="date" value={formData.withdrawal_date} onChange={(e) => setFormData(prev => ({ ...prev, withdrawal_date: e.target.value }))} max={new Date().toISOString().split('T')[0]} className="w-full px-3 py-2 border border-gray-300 rounded-lg" data-testid="withdrawal-date" />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Reason for Withdrawal *</label>
                  <select
                    value={formData.reason}
                    onChange={(e) => setFormData(prev => ({ ...prev, reason: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    required
                  >
                    <option value="">Select Reason</option>
                    {reasons.map((reason) => (
                      <option key={reason} value={reason}>{reason}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Refund Mode *</label>
                  <select
                    value={formData.refund_mode}
                    onChange={(e) => setFormData(prev => ({ ...prev, refund_mode: e.target.value, bank_id: '', bank_name: '' }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="Cash">Cash</option>
                    <option value="Bank Transfer">Bank Transfer</option>
                    <option value="UPI">UPI</option>
                    <option value="Cheque">Cheque</option>
                  </select>
                </div>
              </div>
              
              {formData.refund_mode !== 'Cash' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Select Bank *</label>
                    <select
                      value={formData.bank_id}
                      onChange={(e) => {
                        const bank = banks.find(b => b.id === e.target.value);
                        setFormData(prev => ({ ...prev, bank_id: e.target.value, bank_name: bank?.bank_name || '' }));
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    >
                      <option value="">Select Bank</option>
                      {banks.map((bank) => (
                        <option key={bank.id} value={bank.id}>{bank.bank_name} - {bank.account_number}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Transaction Reference</label>
                    <input
                      type="text"
                      value={formData.transaction_ref}
                      onChange={(e) => setFormData(prev => ({ ...prev, transaction_ref: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      placeholder="UTR / Cheque No."
                    />
                  </div>
                </div>
              )}
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Remarks</label>
                <textarea
                  value={formData.reason_details}
                  onChange={(e) => setFormData(prev => ({ ...prev, reason_details: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  rows={2}
                  placeholder="Enter any additional details..."
                />
              </div>
            </CardContent>
          </Card>

          {/* Summary & Submit */}
          <Card className="border-2 border-green-200">
            <CardContent className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-center">
                <div className="text-center p-3 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-500">Gross Refund</p>
                  <p className="text-xl font-bold">₹{calculateGrossRefund().toLocaleString('en-IN')}</p>
                </div>
                <div className="text-center p-3 bg-red-50 rounded-lg">
                  <p className="text-sm text-gray-500">Deductions</p>
                  <p className="text-xl font-bold text-red-600">- ₹{calculateTotalDeductions().toLocaleString('en-IN')}</p>
                </div>
                <div className="text-center p-4 bg-green-100 rounded-lg col-span-2">
                  <p className="text-sm text-green-700">Net Refund Amount</p>
                  <p className="text-3xl font-bold text-green-700">₹{calculateNetRefund().toLocaleString('en-IN')}</p>
                </div>
              </div>
              
              <div className="mt-6 flex justify-end gap-3">
                <Button variant="outline" onClick={resetForm}>Cancel</Button>
                <Button 
                  onClick={handleSubmit}
                  disabled={processing || !formData.reason}
                  className="bg-red-600 hover:bg-red-700"
                >
                  {processing ? 'Processing...' : (
                    <>
                      <UserMinus className="h-4 w-4 mr-2" />
                      Process Withdrawal
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
